import type React from "react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import type { RootState } from "~/store";
import "react-tabs/style/react-tabs.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import knowledgeStyles from "~/components/Project/knowledge/index.module.scss";
import KnowledgeTable from "~/components/Project/knowledge/KnowledgeTable";
import { CommonButton } from "~/components/ui/commonButton";
import { MAX_ACTIVE_KNOWLEDGE_PER_TYPE } from "~/constants/knowledge";
import { TOAST_MESSAGES } from "~/constants/messages";
import { type SampleKnowledgeItem, sampleKnowledgeItems } from "~/data/sampleProject";
import { useTutorialAccessGuard } from "~/hooks/useTutorialAccessGuard";
import AddWhiteIcon from "~/images/add_white_icon.svg?react";
import type { Knowledge } from "~/interface/knowledge";
import projectTopStyles from "~/pages/projectTop/index.module.scss";
import styles from "./index.module.scss";

const DOCUMENT_TYPES = [
  { key: "system-proposal", label: "システム概要書" },
  { key: "functional-requirements", label: "機能要件書" },
  { key: "non-functional-requirements", label: "非機能要件書" },
  { key: "app-design", label: "アプリ設計書" },
  { key: "field-design", label: "フィールド設計書" },
  { key: "customize-specification", label: "カスタマイズ仕様書" },
] as const;

type DocumentType = (typeof DOCUMENT_TYPES)[number]["key"];

const PROJECT_TOP_TABS = [
  { key: "dashboard", label: "ダッシュボード", route: "/tutorial/project-top" },
  { key: "info", label: "詳細情報", route: "/tutorial/project-top?tab=info" },
  { key: "members", label: "メンバー", route: "/tutorial/project-top?tab=members" },
  { key: "knowledge", label: "ナレッジ", route: "/tutorial/knowledge" },
] as const;

const transformSampleKnowledge = (item: SampleKnowledgeItem): Knowledge => ({
  id: item.id,
  organization_id: "tutorial-org",
  project_id: "tutorial-sample-project",
  type: item.type,
  title: item.title,
  meta_prompt: item.metaPrompt,
  status: item.status,
  isActive: item.status === "approved",
  created_at: item.updatedAt,
  modified_at: item.updatedAt,
  modified_by: "REQXIGN AI",
});

interface KnowledgeContentProps {
  showProjectTabs?: boolean;
  tourId?: string | null;
}

const KnowledgeContent: React.FC<KnowledgeContentProps> = ({
  showProjectTabs = false,
  tourId = "knowledge-content",
}) => {
  const navigate = useNavigate();
  const tourStep = useSelector((state: RootState) => state.auth.tour_step);
  const shouldHighlightTabs = tourStep !== 6; // STEP6では演出をオフ
  const [selectedType, setSelectedType] = useState<DocumentType>("system-proposal");
  const [searchQuery, setSearchQuery] = useState("");
  const [knowledgeRecords, setKnowledgeRecords] = useState<Knowledge[]>(() =>
    sampleKnowledgeItems.map(transformSampleKnowledge),
  );

  const filteredKnowledges = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return knowledgeRecords.filter((record) => {
      if (record.type !== selectedType) return false;
      if (!query) return true;
      return (
        record.title.toLowerCase().includes(query) ||
        record.meta_prompt.toLowerCase().includes(query)
      );
    });
  }, [knowledgeRecords, searchQuery, selectedType]);

  const documentStats = useMemo(() => {
    return DOCUMENT_TYPES.map(({ key }) => {
      const summary = knowledgeRecords.filter((record) => record.type === key);
      const activeCount = summary.filter((record) => record.isActive).length;
      return {
        key,
        total: summary.length,
        active: activeCount,
      };
    });
  }, [knowledgeRecords]);

  const selectedTypeStats = documentStats.find(({ key }) => key === selectedType) ?? {
    total: 0,
    active: 0,
  };

  const pendingKnowledgeCount = useMemo(
    () => sampleKnowledgeItems.filter((item) => item.status === "pending").length,
    [],
  );
  const knowledgeTabIndex = PROJECT_TOP_TABS.findIndex((tab) => tab.key === "knowledge");

  const handleSelectTab = (index: number) => {
    if (!showProjectTabs) return false;
    const target = PROJECT_TOP_TABS[index];
    if (!target || target.key === "knowledge") {
      return false;
    }
    navigate(target.route);
    return false;
  };

  const handleToggleActive = (knowledge: Knowledge) => {
    setKnowledgeRecords((prev) =>
      prev.map((record) =>
        record.id === knowledge.id ? { ...record, isActive: !record.isActive } : record,
      ),
    );
  };

  const handleSelectType = (type: DocumentType) => {
    setSelectedType(type);
    setSearchQuery("");
  };

  const selectedTypeLabel = DOCUMENT_TYPES.find((entry) => entry.key === selectedType)?.label ?? "";

  const containerProps =
    tourId !== null && tourId !== undefined ? { "data-tour-id": tourId } : undefined;

  return (
    <div className={styles["page"]} {...(containerProps ?? {})}>
      {showProjectTabs && (
        <div className={styles["projectTabs"]}>
          <Tabs
            className={projectTopStyles["tab"]}
            selectedIndex={knowledgeTabIndex}
            onSelect={handleSelectTab}
          >
            <TabList className={projectTopStyles["tab-list"]}>
              {PROJECT_TOP_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  className={[
                    projectTopStyles["tab-list-name"],
                    shouldHighlightTabs ? styles["tabHighlight"] : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  selectedClassName={projectTopStyles["tab-list-name--selected"]}
                  disabled
                >
                  <span>{tab.label}</span>
                  {tab.key === "knowledge" && pendingKnowledgeCount > 0 && (
                    <span className={projectTopStyles["knowledge-badge"]}>
                      {pendingKnowledgeCount}
                    </span>
                  )}
                </Tab>
              ))}
            </TabList>
            {PROJECT_TOP_TABS.map((tab) => (
              <TabPanel key={tab.key} style={{ display: "none" }} />
            ))}
          </Tabs>
          <p className={styles["tabHint"]}>
            プロジェクトトップのタブから「ナレッジ」を開くと、この画面に移動します。
          </p>
        </div>
      )}

      <div className={knowledgeStyles["container"]}>
        <div className={knowledgeStyles["titleWithTooltip"]}>
          <h2 className={knowledgeStyles["title"]}>ナレッジ</h2>
        </div>

        <div className={knowledgeStyles["wrapper"]}>
          <ul className={knowledgeStyles["list"]}>
            {DOCUMENT_TYPES.map(({ key, label }) => {
              const isSelected = key === selectedType;
              return (
                <li
                  key={key}
                  className={isSelected ? knowledgeStyles["selected"] : ""}
                  onClick={() => handleSelectType(key)}
                  style={{ cursor: "pointer" }}
                >
                  <span>{label}</span>
                </li>
              );
            })}
          </ul>

          <div className={knowledgeStyles["content"]}>
            <div className={styles["headerRow"]}>
              <div className={styles["headerMeta"]}>
                <h1 className={knowledgeStyles["headerTitle"]}>{selectedTypeLabel}</h1>
                <span className={knowledgeStyles["knowledgeStats"]}>
                  有効: {selectedTypeStats.active}/{MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件
                </span>
                <span className={knowledgeStyles["knowledgeStatsTotal"]}>
                  総数: {selectedTypeStats.total}件
                </span>
              </div>
              <div className={styles["headerControls"]}>
                <div className={`${knowledgeStyles["searchBar"]} ${styles["searchFlexible"]}`}>
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="タイトルやプロンプトで検索..."
                    className={knowledgeStyles["searchInput"]}
                  />
                </div>
                <CommonButton
                  className={styles["createButton"] || ""}
                  icon={<AddWhiteIcon />}
                  onClick={() => toast.info(TOAST_MESSAGES.TUTORIAL.KNOWLEDGE_ADD_DISABLED)}
                >
                  新規追加
                </CommonButton>
              </div>
            </div>

            <div className={styles["tableSection"]}>
              {filteredKnowledges.length > 0 ? (
                <KnowledgeTable
                  knowledges={filteredKnowledges}
                  allKnowledges={knowledgeRecords}
                  onToggleActive={handleToggleActive}
                  searchQuery={searchQuery}
                />
              ) : (
                <div className={knowledgeStyles["emptyState"]}>
                  <p>
                    選択中のドキュメントタイプに表示できるサンプルはありません。
                    条件を変えて確認してください。
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TutorialKnowledgeContent = KnowledgeContent;

const TutorialKnowledge: React.FC = () => {
  useTutorialAccessGuard();
  return <KnowledgeContent showProjectTabs tourId="knowledge-content" />;
};

export default TutorialKnowledge;
